import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useRef, useState, useCallback, useEffect } from 'react';
import ModalContent from './ModalContent.js';
import { Modal, ModalContentContainer } from './Modal.style.js';
import { jsx } from 'react/jsx-runtime';
import useIsMobile from '../../hooks/useIsMobile.js';
import usePrevious from '../../hooks/usePrevious.js';
import useFocusTrap from '../../hooks/useFocusTrap.js';
import useOnClickOutside from '../../hooks/useOnClickOutside.js';
import useDisableScroll from '../../hooks/useDisableScroll.js';

var _excluded = ["children", "headerContent", "heading", "isOpen", "nonClosable", "onClose", "overlaidCloseButton", "size"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var BaseModal = function BaseModal(_ref) {
  var children = _ref.children,
    _ref$headerContent = _ref.headerContent,
    headerContent = _ref$headerContent === void 0 ? null : _ref$headerContent,
    _ref$heading = _ref.heading,
    heading = _ref$heading === void 0 ? null : _ref$heading,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === void 0 ? true : _ref$isOpen,
    _ref$nonClosable = _ref.nonClosable,
    nonClosable = _ref$nonClosable === void 0 ? false : _ref$nonClosable,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === void 0 ? function () {} : _ref$onClose,
    _ref$overlaidCloseBut = _ref.overlaidCloseButton,
    overlaidCloseButton = _ref$overlaidCloseBut === void 0 ? false : _ref$overlaidCloseBut,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'm' : _ref$size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var modalRef = useRef(null);
  var contentRef = useRef(null);
  var isMobile = useIsMobile();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isClosing = _useState2[0],
    setIsClosing = _useState2[1];
  var prevIsOpen = usePrevious(isOpen);
  var closeModal = useCallback(function () {
    if (nonClosable) {
      return;
    }
    setIsClosing(true);
    setTimeout(function () {
      modalRef.current.close();
      setIsClosing(false);
      onClose();
    }, isMobile ? 0 : 200);
  }, [isMobile, nonClosable, onClose]);
  useEffect(function () {
    if (!isOpen) return undefined;
    var modalElement = modalRef.current;
    modalElement.showModal();
    if (!nonClosable) {
      // If closable, add listener on esc key.
      var handleEscapeKeyPress = function handleEscapeKeyPress(event) {
        if (event.key === 'Escape') closeModal();
      };
      var handleCancel = function handleCancel(event) {
        event.preventDefault();
        closeModal();
      };
      modalElement.addEventListener('cancel', handleCancel);
      modalElement.addEventListener('keydown', handleEscapeKeyPress);
      return function () {
        modalElement.removeEventListener('cancel', handleCancel);
        modalElement.removeEventListener('keydown', handleEscapeKeyPress);
      };
    }
    // If not closable, prevent modal from closing.
    var preventDefault = function preventDefault(event) {
      event.preventDefault();
    };
    modalElement.addEventListener('cancel', preventDefault);
    return function () {
      modalElement.removeEventListener('cancel', preventDefault);
    };
  }, [isOpen, closeModal, nonClosable]);
  useEffect(function () {
    if (prevIsOpen && !isOpen && !isClosing) {
      closeModal();
    }
  }, [isOpen, closeModal, prevIsOpen, isClosing]);
  useEffect(function () {
    if (typeof dialogPolyfill !== 'undefined' && typeof HTMLDialogElement !== 'function') {
      var dialog = modalRef.current;
      dialogPolyfill.registerDialog(dialog);
    }
  }, []);
  useFocusTrap(modalRef, isOpen);
  useOnClickOutside(contentRef, closeModal, !isMobile && isOpen);
  useDisableScroll(isOpen);
  return /*#__PURE__*/jsx(Modal, _objectSpread(_objectSpread({
    // @ts-ignore
    $isClosing: isClosing,
    $isOpen: isOpen,
    $size: size,
    "aria-hidden": !isOpen,
    "aria-labelledby": "modal-heading",
    "aria-modal": true,
    ref: modalRef,
    tabIndex: -1
  }, rest), {}, {
    children: /*#__PURE__*/jsx(ModalContentContainer, {
      ref: contentRef,
      children: /*#__PURE__*/jsx(ModalContent, {
        headerContent: headerContent,
        heading: heading,
        onClose: !nonClosable ? closeModal : null,
        overlaidCloseButton: overlaidCloseButton,
        children: children
      })
    })
  }));
};
var BaseModal$1 = BaseModal;

export { BaseModal$1 as default };
//# sourceMappingURL=BaseModal.js.map
